// ═══════════════════════════════════════════════════════════════
// SHELTER MODULE — Vehicles CRUD
// Vehicle cataloging: make/model, plates, owner, parking
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  Textarea, ActionIcon, Modal, Stack, SimpleGrid, Drawer, Divider,
} from '@mantine/core';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh, IconCamera,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { useShelterStore } from '../../store/shelterStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import type { ShelterVehicle, VehicleStatus } from '../../types/shelter';

const STATUS_OPTIONS: { value: VehicleStatus; label: string }[] = [
  { value: 'parked', label: 'Parked' },
  { value: 'released', label: 'Released' },
  { value: 'impounded', label: 'Impounded' },
];

const VEHICLE_STATUS_COLORS: Record<VehicleStatus, string> = {
  parked: 'green',
  released: 'gray',
  impounded: 'red',
};

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC',
].map(s => ({ value: s, label: s }));

const EMPTY: Partial<ShelterVehicle> = {
  make: '', model: '', year: '', color: '', licensePlate: '', state: '',
  vin: '', ownerId: '', shelterId: '', qrCode: '', parkingLocation: '',
  status: 'parked', photoUrl: '', notes: '',
};

export default function Vehicles() {
  const { activeIncident } = useAuthStore();
  const { canEdit } = useModulePermission('shelter');
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    vehicles, loadingVehicles, survivors, shelters,
    loadVehicles, loadSurvivors, loadShelters, createVehicle, updateVehicle, deleteVehicle,
  } = useShelterStore();

  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const [sortField, setSortField] = useState<string>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const sortIcon = (f: string) => sortField === f ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ShelterVehicle | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ShelterVehicle | null>(null);
  const [detailTarget, setDetailTarget] = useState<ShelterVehicle | null>(null);
  const [form, setForm] = useState<Partial<ShelterVehicle>>({ ...EMPTY });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadVehicles(); loadSurvivors(); loadShelters(); }, [loadVehicles, loadSurvivors, loadShelters]);

  const survivorMap = useMemo(() => {
    const m = new Map<string, string>();
    survivors.forEach(s => m.set(String(s.id), `${s.firstName} ${s.lastName}`.trim()));
    return m;
  }, [survivors]);

  const shelterMap = useMemo(() => {
    const m = new Map<string, string>();
    shelters.forEach(s => m.set(String(s.id), s.name));
    return m;
  }, [shelters]);

  // Store already server-side filters by active incident; use directly
  const iSurvivors = incidentId ? survivors : [];
  const iShelters = incidentId ? shelters : [];

  const survivorOptions = useMemo(
    () => iSurvivors.map(s => ({
      value: String(s.id),
      label: `${s.firstName} ${s.lastName}`.trim() || `Survivor #${s.id}`,
    })),
    [iSurvivors],
  );

  const shelterOptions = useMemo(
    () => iShelters.map(s => ({ value: String(s.id), label: s.name || `Shelter #${s.id}` })),
    [iShelters],
  );

  const iVehicles = incidentId ? vehicles : [];

  const filtered = useMemo(() => {
    let list = iVehicles;
    if (statusFilter && statusFilter !== 'all') list = list.filter(v => v.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(v =>
        v.make.toLowerCase().includes(q) ||
        v.model.toLowerCase().includes(q) ||
        v.licensePlate.toLowerCase().includes(q) ||
        v.color.toLowerCase().includes(q) ||
        (survivorMap.get(v.ownerId) ?? '').toLowerCase().includes(q),
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (!sortField) return `${a.make} ${a.model}`.localeCompare(`${b.make} ${b.model}`);
      const va = (a as any)[sortField] ?? '';
      const vb = (b as any)[sortField] ?? '';
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [iVehicles, statusFilter, search, survivorMap, sortField, sortDir]);

  const setField = (key: keyof ShelterVehicle, value: string) => setForm(prev => ({ ...prev, [key]: value }));

  const openCreate = () => { setEditTarget(null); setForm({ ...EMPTY }); setModalOpen(true); };
  const openEdit = (v: ShelterVehicle) => { setEditTarget(v); setForm({ ...v }); setModalOpen(true); };

  const handleSave = async () => {
    if (!form.make?.trim() && !form.model?.trim()) {
      notifications.show({ message: 'Make or model is required', color: 'yellow' });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, incidentId };
      if (editTarget) {
        await updateVehicle(editTarget.id, payload);
        notifications.show({ message: 'Vehicle updated', color: 'green' });
      } else {
        await createVehicle(payload);
        notifications.show({ message: 'Vehicle created', color: 'green' });
      }
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteVehicle(deleteTarget.id);
      notifications.show({
        message: `${deleteTarget.year} ${deleteTarget.make} ${deleteTarget.model} deleted`,
        color: 'gray',
      });
      setDeleteTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'red' });
    } finally { setSaving(false); }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Vehicles</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingVehicles} onClick={() => loadVehicles()}>Refresh</Button>
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Add Vehicle</Button>}
        </Group>
      </Group>

      <Group mb="md" gap="sm">
        <TextInput placeholder="Search make, model, plate, color, owner..."
          leftSection={<IconSearch size={14} />} value={search}
          onChange={e => setSearch(e.currentTarget.value)} size="xs"
          style={{ flex: 1, minWidth: 200 }} />
        <Select data={[{ value: 'all', label: 'All Statuses' }, ...STATUS_OPTIONS]}
          value={statusFilter ?? 'all'} onChange={v => setStatusFilter(v)} size="xs" w={160} />
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} vehicle{filtered.length !== 1 ? 's' : ''}</Text>

      <Paper withBorder>
        <div style={{ overflowX: 'auto', maxHeight: 500, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--mantine-color-gray-3)' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Vehicle</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Color</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('licensePlate')}>License Plate{sortIcon('licensePlate')}</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>State</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Owner</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Shelter</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Parking</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('status')}>Status{sortIcon('status')}</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={9} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                  {loadingVehicles ? 'Loading...' : 'No vehicles found'}
                </td></tr>
              )}
              {filtered.map(v => (
                <tr key={v.id} onClick={() => setDetailTarget(v)} style={{ cursor: 'pointer', borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
                  <td style={{ padding: '8px 12px', fontWeight: 500 }}>
                    {[v.year, v.make, v.model].filter(Boolean).join(' ') || '-'}
                  </td>
                  <td style={{ padding: '8px 12px' }}>{v.color || '-'}</td>
                  <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{v.licensePlate || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>{v.state || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>{survivorMap.get(v.ownerId) || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>{shelterMap.get(v.shelterId) || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>{v.parkingLocation || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>
                    <Badge size="sm" color={VEHICLE_STATUS_COLORS[v.status] ?? 'gray'}>{v.status}</Badge>
                  </td>
                  {canEdit && (
                  <td style={{ padding: '8px 12px', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                    <Group gap={4} justify="flex-end">
                      <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(v)}>
                        <IconPencil size={14} />
                      </ActionIcon>
                      <ActionIcon size="sm" variant="subtle" color="red" onClick={() => setDeleteTarget(v)}>
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Group>
                  </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Paper>

      {/* Create / Edit Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Vehicle' : 'Add Vehicle'} centered size="lg">
        <Stack>
          <Text size="sm" fw={600} c="dimmed">Vehicle Info</Text>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
            <TextInput label="Make" value={form.make ?? ''}
              onChange={e => setField('make', e.currentTarget.value)} />
            <TextInput label="Model" value={form.model ?? ''}
              onChange={e => setField('model', e.currentTarget.value)} />
            <TextInput label="Year" value={form.year ?? ''}
              onChange={e => setField('year', e.currentTarget.value)} />
          </SimpleGrid>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
            <TextInput label="Color" value={form.color ?? ''}
              onChange={e => setField('color', e.currentTarget.value)} />
            <TextInput label="License Plate" value={form.licensePlate ?? ''}
              onChange={e => setField('licensePlate', e.currentTarget.value)} />
            <Select label="State" data={US_STATES} value={form.state ?? ''}
              onChange={v => setField('state', v ?? '')} searchable clearable />
          </SimpleGrid>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput label="VIN" value={form.vin ?? ''}
              onChange={e => setField('vin', e.currentTarget.value)} />
            <Select label="Status" data={STATUS_OPTIONS}
              value={form.status ?? 'parked'}
              onChange={v => setField('status', v ?? 'parked')} />
          </SimpleGrid>

          <Text size="sm" fw={600} c="dimmed" mt="sm">Assignment</Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <Select label="Owner" data={survivorOptions} value={form.ownerId ?? ''}
              onChange={v => setField('ownerId', v ?? '')} searchable clearable />
            <Select label="Shelter" data={shelterOptions} value={form.shelterId ?? ''}
              onChange={v => setField('shelterId', v ?? '')} searchable clearable />
            <TextInput label="Parking Location" value={form.parkingLocation ?? ''}
              onChange={e => setField('parkingLocation', e.currentTarget.value)} />
            <TextInput label="QR Code" value={form.qrCode ?? ''}
              onChange={e => setField('qrCode', e.currentTarget.value)} />
          </SimpleGrid>

          <div>
            <Text size="sm" fw={500} mb={4}>Photo</Text>
            {form.photoUrl && (
              <img src={form.photoUrl} alt="Vehicle" style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 8, marginBottom: 8 }} />
            )}
            <Button
              variant="light"
              size="xs"
              leftSection={<IconCamera size={14} />}
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'image/*';
                if (/Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)) {
                  input.capture = 'environment';
                }
                input.onchange = (e) => {
                  const file = (e.target as HTMLInputElement).files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = () => setField('photoUrl', reader.result as string);
                  reader.readAsDataURL(file);
                };
                input.click();
              }}
            >
              {form.photoUrl ? 'Change Photo' : (/Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ? 'Take Photo' : 'Upload Photo')}
            </Button>
          </div>
          <Textarea label="Notes" value={form.notes ?? ''}
            onChange={e => setField('notes', e.currentTarget.value)} autosize minRows={2} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>
              {editTarget ? 'Save Changes' : 'Create Vehicle'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Vehicle" centered size="sm">
        <Stack>
          <Text size="sm">Delete <strong>{deleteTarget ? `${deleteTarget.year} ${deleteTarget.make} ${deleteTarget.model}` : ''}</strong>? This cannot be undone.</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="red" size="xs" loading={saving} onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Detail Drawer */}
      <Drawer opened={!!detailTarget} onClose={() => setDetailTarget(null)} title="Vehicle Details"
        position="right" size="md">
        {detailTarget && (
          <Stack>
            <Group>
              <Text size="xl" fw={700}>
                {[detailTarget.year, detailTarget.make, detailTarget.model].filter(Boolean).join(' ') || 'Unknown Vehicle'}
              </Text>
              {detailTarget.color && <Badge color="gray">{detailTarget.color}</Badge>}
            </Group>

            <Divider />

            <SimpleGrid cols={2}>
              <div>
                <Text size="xs" c="dimmed">License Plate</Text>
                <Text size="sm" ff="monospace">{detailTarget.licensePlate || '-'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">State</Text>
                <Text size="sm">{detailTarget.state || '-'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">VIN</Text>
                <Text size="sm" ff="monospace">{detailTarget.vin || '-'}</Text>
              </div>
            </SimpleGrid>

            <Divider />

            <div>
              <Text size="xs" c="dimmed">Owner</Text>
              {detailTarget.ownerId && survivorMap.get(detailTarget.ownerId) ? (
                <Text size="sm" c="blue" style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() => { setDetailTarget(null); navigate('/shelter/survivor/' + detailTarget.ownerId); }}>
                  {survivorMap.get(detailTarget.ownerId)} — View Owner
                </Text>
              ) : (
                <Text size="sm">-</Text>
              )}
            </div>

            <SimpleGrid cols={2}>
              <div>
                <Text size="xs" c="dimmed">Shelter</Text>
                <Text size="sm">{shelterMap.get(detailTarget.shelterId) || '-'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">Parking Location</Text>
                <Text size="sm">{detailTarget.parkingLocation || '-'}</Text>
              </div>
            </SimpleGrid>

            <div>
              <Text size="xs" c="dimmed">QR Code</Text>
              <Text size="sm" ff="monospace">{detailTarget.qrCode || '-'}</Text>
            </div>

            <Divider />

            <div>
              <Text size="xs" c="dimmed">Status</Text>
              <Badge color={VEHICLE_STATUS_COLORS[detailTarget.status] ?? 'gray'}>{detailTarget.status}</Badge>
            </div>

            <div>
              <Text size="xs" c="dimmed">Notes</Text>
              <Text size="sm">{detailTarget.notes || '-'}</Text>
            </div>

            <Divider />

            {canEdit && (
            <Group>
              <Button size="xs" leftSection={<IconPencil size={14} />}
                onClick={() => { const v = detailTarget; setDetailTarget(null); openEdit(v); }}>
                Edit
              </Button>
              <Button size="xs" color="red" leftSection={<IconTrash size={14} />}
                onClick={() => { const v = detailTarget; setDetailTarget(null); setDeleteTarget(v); }}>
                Delete
              </Button>
            </Group>
            )}
          </Stack>
        )}
      </Drawer>
    </div>
  );
}
